import { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Table, Button, InputNumber,
  Space, message, Divider
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../api';

const TAX_RATES = [
  { value: 0, label: '0 %' },
  { value: 7, label: '7 %' },
  { value: 19, label: '19 %' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Entwurf' },
  { value: 'sent', label: 'Gesendet' },
  { value: 'paid', label: 'Bezahlt' },
  { value: 'cancelled', label: 'Storniert' },
];

const CATEGORIES = [
  'Arbeitsmittel',
  'Bewirtung',
  'Fortbildung',
  'Fremdleistungen',
  'Geringwertige Wirtschaftsgüter',
  'Geschenke',
  'Laufende EDV-Kosten',
  'Sonstige',
  'Sonstige tatsächliche Fahrtkosten',
  'Telekommunikation',
  'Verpackung und Transport',
  'Wareneinkauf',
  'Werbekosten',
].map(c => ({ value: c, label: c }));

function calcTotals(items) {
  let netTotal = 0;
  let taxTotal = 0;
  for (const it of items) {
    const net = (it.quantity || 0) * (it.unit_price_net || 0);
    netTotal += net;
    taxTotal += net * ((it.tax_rate || 0) / 100);
  }
  return { netTotal, taxTotal, grossTotal: netTotal + taxTotal };
}

function fmt(n) {
  return Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InvoiceForm({ open, invoiceType, invoice, onSave, onClose }) {
  const [form] = Form.useForm();
  const [items, setItems] = useState([{ key: 0, description: '', quantity: 1, unit: '', unit_price_net: 0, tax_rate: 19 }]);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const contactType = invoiceType === 'incoming' ? 'supplier' : 'customer';
    api.contacts.list(contactType).then(setContacts).catch(() => {});
  }, [open, invoiceType]);

  useEffect(() => {
    if (!open) return;
    if (invoice) {
      form.setFieldsValue({
        invoice_number: invoice.invoice_number,
        contact_id: invoice.contact_id,
        invoice_date: dayjs(invoice.invoice_date),
        payment_date: invoice.payment_date ? dayjs(invoice.payment_date) : null,
        notes: invoice.notes,
        status: invoice.status,
        category: invoice.category || null,
      });
      setItems((invoice.items || []).map((it, i) => ({ ...it, key: i })));
    } else {
      form.resetFields();
      form.setFieldsValue({ status: 'draft' });
      setItems([{ key: 0, description: '', quantity: 1, unit: '', unit_price_net: 0, tax_rate: 19 }]);
    }
  }, [open, invoice, form]);

  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(it => it.key === key ? { ...it, [field]: value } : it));
  };

  const addItem = () => {
    const nextKey = Math.max(...items.map(i => i.key), 0) + 1;
    setItems(prev => [...prev, { key: nextKey, description: '', quantity: 1, unit: '', unit_price_net: 0, tax_rate: 19 }]);
  };

  const removeItem = (key) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(it => it.key !== key));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!items.some(it => it.description)) {
        message.error('Mindestens eine Position mit Beschreibung ist erforderlich');
        return;
      }
      setSaving(true);
      const payload = {
        ...values,
        type: invoiceType,
        invoice_date: values.invoice_date.format('YYYY-MM-DD'),
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD') : null,
        category: values.category || null,
        items: items.filter(it => it.description),
      };
      let result;
      if (invoice) {
        result = await api.invoices.update(invoice.id, payload);
      } else {
        result = await api.invoices.create(payload);
      }
      message.success('Rechnung gespeichert');
      onSave(result);
    } catch (e) {
      if (e.message) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const { netTotal, taxTotal, grossTotal } = calcTotals(items);

  const itemColumns = [
    {
      title: 'Pos.', width: 50,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Beschreibung', dataIndex: 'description',
      render: (val, record) => (
        <Input
          value={val}
          onChange={e => updateItem(record.key, 'description', e.target.value)}
          placeholder="Beschreibung"
        />
      ),
    },
    {
      title: 'Menge', dataIndex: 'quantity', width: 90,
      render: (val, record) => (
        <InputNumber
          value={val}
          min={0}
          step={0.1}
          style={{ width: '100%' }}
          onChange={v => updateItem(record.key, 'quantity', v)}
        />
      ),
    },
    {
      title: 'Einheit', dataIndex: 'unit', width: 80,
      render: (val, record) => (
        <Input value={val} onChange={e => updateItem(record.key, 'unit', e.target.value)} placeholder="Stk." />
      ),
    },
    {
      title: 'EP Netto (€)', dataIndex: 'unit_price_net', width: 130,
      render: (val, record) => (
        <InputNumber
          value={val}
          min={0}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          onChange={v => updateItem(record.key, 'unit_price_net', v)}
        />
      ),
    },
    {
      title: 'MwSt.', dataIndex: 'tax_rate', width: 90,
      render: (val, record) => (
        <Select
          value={val}
          style={{ width: '100%' }}
          options={TAX_RATES}
          onChange={v => updateItem(record.key, 'tax_rate', v)}
        />
      ),
    },
    {
      title: 'Netto (€)', width: 110,
      render: (_, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmt((record.quantity || 0) * (record.unit_price_net || 0))}
        </span>
      ),
    },
    {
      title: 'MwSt. (€)', width: 110,
      render: (_, record) => {
        const net = (record.quantity || 0) * (record.unit_price_net || 0);
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(net * (record.tax_rate / 100))}</span>;
      },
    },
    {
      title: '', width: 40,
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={items.length === 1}
          onClick={() => removeItem(record.key)}
        />
      ),
    },
  ];

  const title = invoiceType === 'incoming'
    ? (invoice ? 'Eingangsrechnung bearbeiten' : 'Neue Eingangsrechnung')
    : (invoice ? 'Ausgangsrechnung bearbeiten' : 'Neue Ausgangsrechnung');

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleSave}
      onCancel={onClose}
      okText="Speichern"
      cancelText="Abbrechen"
      confirmLoading={saving}
      width={1100}
      style={{ top: 20 }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Space wrap style={{ width: '100%', marginBottom: 0 }}>
          <Form.Item name="invoice_number" label="Rechnungsnummer" rules={[{ required: true, message: 'Pflichtfeld' }]} style={{ minWidth: 200 }}>
            <Input />
          </Form.Item>
          <Form.Item
            name="contact_id"
            label={invoiceType === 'incoming' ? 'Lieferant' : 'Kunde'}
            rules={[{ required: true, message: 'Pflichtfeld' }]}
            style={{ minWidth: 250 }}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={contacts.map(c => ({ value: c.id, label: c.name }))}
              placeholder="Kontakt auswählen"
            />
          </Form.Item>
          <Form.Item name="invoice_date" label="Rechnungsdatum" rules={[{ required: true, message: 'Pflichtfeld' }]}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="payment_date" label="Zahlungsdatum">
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select style={{ minWidth: 140 }} options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name="category" label="Kategorie" style={{ minWidth: 240 }}>
            <Select
              allowClear
              placeholder="Kategorie wählen"
              options={CATEGORIES}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Space>
        <Form.Item name="notes" label="Notizen">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>

      <Divider>Positionen</Divider>

      <Table
        rowKey="key"
        columns={itemColumns}
        dataSource={items}
        pagination={false}
        size="small"
        style={{ marginBottom: 8 }}
      />
      <Button icon={<PlusOutlined />} onClick={addItem} size="small" style={{ marginBottom: 16 }}>
        Position hinzufügen
      </Button>

      <Divider />
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <Space direction="vertical" size={2} style={{ minWidth: 280 }}>
          <div><span style={{ marginRight: 24 }}>Netto gesamt:</span><strong>{fmt(netTotal)} €</strong></div>
          <div><span style={{ marginRight: 24 }}>MwSt. gesamt:</span><strong>{fmt(taxTotal)} €</strong></div>
          <div style={{ fontSize: 16, borderTop: '1px solid #d9d9d9', paddingTop: 4, marginTop: 4 }}>
            <span style={{ marginRight: 24 }}>Brutto gesamt:</span><strong>{fmt(grossTotal)} €</strong>
          </div>
        </Space>
      </div>
    </Modal>
  );
}
